import { AddIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Skeleton,
  SkeletonCircle,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import AppTableWithPageMeta from 'components/AppTableWithPageMeta';
import DeleteConfirmationModal from 'components/genericForms/DeleteConfirmationModal';
import currencySymbolMap from 'currency-symbol-map';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlineDocumentDuplicate } from "react-icons/hi";
import { MdDelete, MdEdit } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  useDeleteCampaignMutation,
  useDuplicateCampaignMutation,
  useGetcampaignsInsightsQuery,
  useGetcampaignsListQuery,
  useUpdateCampaignStatusMutation
} from 'store/campaigns/campaignsApi';
import {
  selectAfter,
  selectBefore,
  selectCampaignlistLoading,
  selectCampaignsForInsights,
  selectCurrentPage,
  selectSelectedCampaignId,
  setAfter,
  setBefore,
  setCampaignListLoading,
  setCampaignsForInsights,
  setCurrentPage,
  setSelectedCampaignId
} from 'store/campaigns/campaignsSlice';
import CreateCampaignForm from './Forms/CampaignForms/CreateCampaignForm';
import UpdateCampaignForm from './Forms/CampaignForms/UpdateCampaignForm';

function CampaignsTable({ adsAccountData, startDate, endDate, datePreset, setSelectedTab, onRefresh, onRefreshComplete }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const isMounted = useRef(false);
  const urlInitDoneRef = useRef(false);
  const prevAccountIdRef = useRef(adsAccountData?.account_id);
  const selectedCampaignId = useSelector(selectSelectedCampaignId);
  const campaignsForInsights = useSelector(selectCampaignsForInsights);
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const isCampaignsListLoading = useSelector(selectCampaignlistLoading);
  const after = useSelector(selectAfter);
  const before = useSelector(selectBefore);
  const currentPage = useSelector(selectCurrentPage);
  const isSearchDisabled = Boolean(selectedCampaignId);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDuplicateModalOpen, onOpen: onDuplicateModalOpen, onClose: onDuplicateModalClose } = useDisclosure();

  const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [campaignsListWithInsights, setCampaignsListWithInsights] = useState([]);
  const [switchStates, setSwitchStates] = useState({});
  const [copyCount, setCopyCount] = useState(1);

  // Local loader for stitched list
  const [isLoadingCampaignsListWithInsights, setIsLoadingCampaignsListWithInsights] = useState(true);

  // New: pending created id & polling tries
  const [pendingCreatedId, setPendingCreatedId] = useState(null);
  const [pendingTries, setPendingTries] = useState(0);

  const currencyCode = adsAccountData?.currency;
  const currencySymbol = currencySymbolMap(adsAccountData?.currency);

  const {
    data: campaignsList,
    isFetching: isCampaignListFetcing,
    isUninitialized: isCampaignListUninitialized,
    refetch: refetchCampaignsList,
    isLoading: isCampaignListLoading,
    error: campaignsListError
  } = useGetcampaignsListQuery(
    {
      pageSize: '20',
      ...(searchValue ? {} : { after, before }),
      searchValue,
      adsAccountId: adsAccountData?.account_id,
      campaign_ids: selectedCampaignId
        ? [selectedCampaignId?.campaign_id ? selectedCampaignId?.campaign_id : selectedCampaignId]
        : [],
    },
    { skip: !adsAccountData?.account_id }
  );

  const {
    data: campaignsInsights,
    isFetching: isCampaignInsightsListFetcing,
    refetch: refetchCampaignsInsights,
    isLoading: isCampaignInsightsLoading,
    error: campaignsInsightsError
  } = useGetcampaignsInsightsQuery(
    {
      searchValue,
      adsAccountId: adsAccountData?.account_id,
      date_preset: datePreset,
      campaigns: campaignsForInsights?.length > 0 ? campaignsForInsights : [],
      ...(datePreset ? {} : { start_date: startDate, end_date: endDate }),
    },
    { skip: !adsAccountData?.account_id || campaignsForInsights?.length === 0 }
  );

  const [updateCampaignStatus] = useUpdateCampaignStatusMutation({});
  const [deleteCampaign] = useDeleteCampaignMutation();
  const [duplicateCampaign, { isLoading: isCampaignDuplicating }] = useDuplicateCampaignMutation();

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Global "refresh list" trigger
  useEffect(() => {
    if (isCampaignsListLoading) {
      dispatch(setSelectedCampaignId(null));
      localStorage.removeItem("cachedCampaign");
      if (!selectedCampaignId) {
        refetchCampaignsList();
        dispatch(setCampaignListLoading(false));
      }
    }
  }, [isCampaignsListLoading, selectedCampaignId, dispatch, refetchCampaignsList]);

  // Refetch when ad account changes
  useEffect(() => {
    if (adsAccountData?.account_id) {
      refetchCampaignsList();
    }
  }, [adsAccountData?.account_id, refetchCampaignsList]);

  // Ensure loader appears immediately when ad account changes
  useEffect(() => {
    const prevId = prevAccountIdRef.current;
    const currId = adsAccountData?.account_id;

    if (prevId && currId && prevId !== currId) {
      setIsLoadingCampaignsListWithInsights(true);
      setSelectedCampaign(null);
      setSwitchStates({});
      setSearchValue('');
      dispatch(setSelectedCampaignId(null));
      dispatch(setAfter(''));
      dispatch(setBefore(''));
      dispatch(setCurrentPage(0));
    }

    prevAccountIdRef.current = currId;
  }, [adsAccountData?.account_id, dispatch]);


  // Handle manual refresh prop
  useEffect(() => {
    if (onRefresh) {
      refetchCampaignsList();
      onRefreshComplete && onRefreshComplete();
    }
  }, [onRefresh, refetchCampaignsList, onRefreshComplete]);

  // Build switch map and insight query input
  useEffect(() => {
    if (campaignsList?.data) {
      const initialSwitchStates = {};
      campaignsList?.data.forEach(campaign => {
        initialSwitchStates[campaign.campaign_id] = campaign.status;
      });
      setSwitchStates(initialSwitchStates);

      const formattedCampaigns = campaignsList?.data.reduce((acc, campaign) => {
        const promotedObject = campaign?.adsets?.[0]?.promoted_object;

        if (campaign?.campaign_id) {
          acc.push({
            campaign_id: campaign?.campaign_id,
            promoted_object: promotedObject,
            destination_type: campaign?.adsets?.[0]?.destination_type,
            optimization_goal: campaign?.adsets?.[0]?.optimization_goal
          });
        } else {
          acc.push({
            campaign_id: campaign?.campaign_id,
            destination_type: campaign?.adsets?.[0]?.destination_type,
            optimization_goal: campaign?.adsets?.[0]?.optimization_goal
          });
        }
        return acc;
      }, []);

      dispatch(setCampaignsForInsights(formattedCampaigns));
    }
  }, [campaignsList, dispatch]);

  // Stitch list + insights; add placeholder row if we're waiting for the created id
  useEffect(() => {
    if (campaignsList?.data || campaignsInsights) {
      const campaignsWithInsights = (campaignsList?.data || []).map(campaign => {
        const insightData = campaignsInsights?.find(insight => insight?.campaign_id === campaign?.campaign_id);
        return {
          ...campaign,
          insight: insightData?.insights || {},
        };
      });

      // Insert placeholder row at the top if waiting for created id and it's not in the list yet
      if (pendingCreatedId) {
        const exists = campaignsWithInsights.some(c => c.campaign_id === pendingCreatedId);
        if (!exists) {
          campaignsWithInsights.unshift({
            campaign_id: pendingCreatedId,
            name: "Creating campaign…",
            effective_status: "IN_PROCESS",
            status: "IN_PROCESS",
            objective: "-",
            created_time: new Date().toISOString(),
            updated_time: new Date().toISOString(),
            insight: {},
            __isPlaceholder: true
          });
        }
      }
      setCampaignsListWithInsights(campaignsWithInsights);

      const t = setTimeout(() => {
        setIsLoadingCampaignsListWithInsights(false);
      }, 100);
      return () => clearTimeout(t);
    }
  }, [campaignsList, campaignsInsights, pendingCreatedId]);

  // Polling loop: check for created id up to 3 times, 3s apart
  useEffect(() => {
    if (!pendingCreatedId) return;

    const found = campaignsList?.data?.some((c) => c.campaign_id === pendingCreatedId) ?? false;

    if (found) {
      setPendingCreatedId(null);
      setPendingTries(0);
      return;
    }

    if (pendingTries >= 3) {
      setPendingCreatedId(null);
      setPendingTries(0);
      return;
    }

    const t = setTimeout(() => {
      setPendingTries((x) => x + 1);
      refetchCampaignsList();
    }, 3000);

    return () => clearTimeout(t);
  }, [pendingCreatedId, campaignsList, pendingTries]);

  const handleSwitchChange = (campaignId, currentState) => {
    const newStatus = currentState === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';

    setSwitchStates(prev => ({ ...prev, [campaignId]: newStatus }));
    updateCampaignStatus({
      campaignId,
      payload: {
        status: newStatus,
        adsAccount: {
          id: adsAccountData?.id,
          adsAccountId: adsAccountData?.account_id,
          adsAccountName: adsAccountData?.name,
          businessName: adsAccountData?.business?.name,
          businessId: adsAccountData?.business?.id
        },
      }
    }).unwrap()
      .then(() => {
        if (isMounted.current) {
          refetchCampaignsList();
          toast({
            position: "top-right",
            title: `Campaign ${newStatus === 'ACTIVE' ? 'activated' : 'paused'}`,
            status: "success",
            duration: 4000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        if (isMounted.current) {
          toast({
            position: "top-right",
            title: "Error updating campaign status",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
        console.error('Error updating campaign status:', error);
        setSwitchStates(prev => ({ ...prev, [campaignId]: currentState }));
      });
  };

  const handleDeleteCampaign = (campaignData) => {
    deleteCampaign({ campaignId: campaignData?.campaign_id, adsAccountId: adsAccountData?.account_id })
      .unwrap()
      .then(() => {
        refetchCampaignsList();
        toast({
          position: "top-right",
          title: "Campaign deleted",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch(error => {
        toast({
          position: "top-right",
          title: "Error deleting campaign",
          status: "error",
          description: error?.data?.message,
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const handleDuplicateCampaign = () => {
    duplicateCampaign({ campaignId: selectedCampaign?.campaign_id, payload: { copy_count: copyCount } })
      .unwrap()
      .then(() => {
        refetchCampaignsList();
        toast({
          position: "top-right",
          title: "Campaign duplicated sucessfully",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch(error => {
        toast({
          position: "top-right",
          title: "Error duplicating campaign",
          status: "error",
          description: error?.data?.message,
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => {
        setSelectedCampaign(null);
        onDuplicateModalClose();
      });
  };

  const handlePageChange = ({ after, before, currentPage }) => {
    if (after) {
      dispatch(setAfter(after));
      dispatch(setBefore(''));
    } else if (before) {
      dispatch(setBefore(before));
      dispatch(setAfter(''));
    }
    dispatch(setCurrentPage(currentPage));
  };


  // 1) On mount, read URL once and seed Redux
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stored = params.get('page_id');
    const parsed = stored !== null ? Number(stored) : NaN;

    if (Number.isInteger(parsed) && parsed >= 0) {
      dispatch(setCurrentPage(parsed));
    } else {
      // write page_id=0 into URL so both stay in sync from the start
      const next = new URLSearchParams(location.search);
      next.set('page_id', '0');
      navigate({ search: next.toString() }, { replace: true });
    }
    urlInitDoneRef.current = true;
  }, []);

  // 2) Whenever currentPage changes, push it to the URL (no readback loop)
  useEffect(() => {
    if (!urlInitDoneRef.current) return;

    const params = new URLSearchParams(location.search);
    const existing = params.get('page_id');
    if (existing === String(currentPage)) return; // already in sync

    params.set('page_id', String(currentPage));
    navigate({ search: params.toString() }, { replace: true });
    // only depend on currentPage & location.search so we re-evaluate when URL actually changes
  }, [currentPage, location.search]);


  const columns = useMemo(
    () => [
      {
        Header: "Off/On",
        accessor: "",
        Filter: "",
        filter: "",
        width: 120,
        Cell: ({ row: { original } }) => {
          const isActive = switchStates[original.campaign_id] ?? (original.status === 'ACTIVE');
          const tooltipText = isActive ? 'Campaign is ON' : 'Campaign is OFF';

          return (
            <Flex alignItems={'center'} justifyContent={'center'}>
              <Tooltip label={tooltipText} fontSize='xs'>
                <FormControl width={'fit-content'}>
                  <Switch
                    colorScheme='brand'
                    size={'sm'}
                    isChecked={original.status === 'ACTIVE'}
                    onChange={() => handleSwitchChange(original.campaign_id, original.status)}
                    isDisabled={userInfo?.role !== 'client' || original.__isPlaceholder}
                  />
                </FormControl>
              </Tooltip>
            </Flex>
          );
        },
      },
      {
        Header: "Name",
        accessor: "name",
        Filter: "",
        filter: "",
        width: 300,
        Cell: ({ row: { original } }) => {
          // Placeholder row
          if (original.__isPlaceholder) {
            return (
              <Flex align="center" gap={3}>
                <SkeletonCircle size="4" />
                <Box>
                  <Skeleton height="12px" width="160px" mb="1" />
                  <Skeleton height="8px" width="90px" />
                </Box>
              </Flex>
            );
          }

          const isSelected = selectedCampaignId?.campaign_id ? selectedCampaignId?.campaign_id : selectedCampaignId === original?.campaign_id;

          return (
            <Box>
              <Box>
                <Flex alignItems={'flex-start'}>
                  <Link
                    to={'#'}
                    onClick={() => {
                      setSelectedTab(1);
                      dispatch(setSelectedCampaignId(original));
                    }}
                  >
                    <Text
                      _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                      fontWeight={isSelected ? 'bold' : 'normal'}
                      color={isSelected ? 'brand.500' : 'inherit'}
                      fontSize={'14px'}
                    >
                      {original.name}
                    </Text>
                  </Link>
                </Flex>
              </Box>
              <Box display={'flex'} dir='row' gap={2}>
                <Flex
                  alignItems={'center'}
                  gap={0.5}
                  onClick={() => {
                    if (userInfo?.role === 'client') {
                      setSelectedCampaign(original);
                      onOpen();
                    }
                  }}>
                  <Icon
                    as={MdEdit}
                    cursor={userInfo?.role === 'client' ? "pointer" : "not-allowed"}
                    color={userInfo?.role === 'client' ? "inherit" : "gray.300"}
                    fontSize={'12px'}
                  />
                  <Link to={'#'}>
                    <Text
                      _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                      fontWeight={'normal'}
                      color={'inherit'}
                      fontSize={'11px'}
                    >
                      Edit
                    </Text>
                  </Link>
                </Flex>
                <Flex
                  alignItems={'center'}
                  gap={0.5}
                  onClick={() => {
                    if (userInfo?.role === 'client') {
                      setSelectedCampaign(original);
                      onDuplicateModalOpen();
                    }
                  }}
                >
                  <Icon
                    as={HiOutlineDocumentDuplicate}
                    cursor={userInfo?.role === 'client' ? "pointer" : "not-allowed"}
                    color={userInfo?.role === 'client' ? "inherit" : "gray.300"}
                    fontSize={'12px'}
                  />
                  <Link to={'#'}>
                    <Text
                      _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                      fontWeight={'normal'}
                      color={'inherit'}
                      fontSize={'11px'}
                    >
                      Duplicate
                    </Text>
                  </Link>
                </Flex>
              </Box>
            </Box>
          );
        },
      },
      {
        Header: "Delivery",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const getStatusMessage = (status) => {
            switch (status) {
              case 'WITH_ISSUES': return 'Delivery error';
              case 'CAMPAIGN_PAUSED': return 'Campaign Off';
              case 'ADSET_PAUSED': return 'Adset Off';
              case 'PAUSED': return 'Off';
              default: return status || 'Unknown';
            }
          };

          const errorMessage = original?.issues_info?.[0]?.error_message || '';

          const getCircleStyles = (status) => {
            switch (status) {
              case 'WITH_ISSUES': return { bg: 'rgb(161,11,24)', border: 'none' };
              case 'CAMPAIGN_PAUSED':
              case 'ADSET_PAUSED': return { bg: 'rgb(216,221,225)', border: 'none' };
              case 'ACTIVE': return { bg: 'rgb(255,255,255)', border: '2px solid green' };
              case 'IN_PROCESS':
              case 'PENDING_REVIEW': return { bg: 'rgb(255,255,255)', border: '2px solid green' };
              default: return { bg: 'rgb(255,255,255)', border: 'none' };
            }
          };

          const circleStyles = getCircleStyles(original?.effective_status);

          const formatTooltipMessage = (message) => {
            return message.split('\n').map((part, index) => (
              <span key={index}>
                {part}
                <br />
              </span>
            ));
          };

          const getTooltipMessage = (status) => {
            switch (status) {
              case 'WITH_ISSUES': return errorMessage;
              case 'PAUSED': return 'This campaign is turned off';
              case 'ACTIVE': return 'This campaign is on and running normally.';
              case 'ADSET_PAUSED': return 'This ad is on, but its ad set is turned off.';
              case 'IN_PROCESS': return 'Your ad is processing.\nMeta is processing your recent updates to your ad. Some updates may take several hours.';
              default: return '';
            }
          };

          const tooltipMessage = getTooltipMessage(original?.effective_status);

          return (
            <Flex>
              {original?.effective_status === 'WITH_ISSUES' ? (
                <Flex alignItems={'center'}>
                  <Box
                    bg={circleStyles.bg}
                    border={circleStyles.border}
                    borderRadius="50%"
                    boxSize="0.8rem"
                    display="inline-block"
                    mr={2}
                  />
                  <Tooltip label={formatTooltipMessage(tooltipMessage)} aria-label="Error message" hasArrow p={2} fontSize={'12px'}>
                    <Text fontSize={'14px'}>{getStatusMessage(original?.effective_status)}</Text>
                  </Tooltip>
                </Flex>
              ) : (
                <Flex alignItems={'center'}>
                  {(original?.effective_status === 'PAUSED' || original?.effective_status === 'ARCHIVED') ? null : (
                    <Box
                      bg={circleStyles.bg}
                      border={circleStyles.border}
                      borderRadius="50%"
                      boxSize="0.8rem"
                      display="inline-block"
                      mr={2}
                    />
                  )}
                  {original?.effective_status !== 'PENDING_REVIEW' && (
                    <Tooltip label={formatTooltipMessage(tooltipMessage)} aria-label="Error message" hasArrow p={2} fontSize={'12px'}>
                      <Text fontSize={'14px'}>{getStatusMessage(original?.effective_status)}</Text>
                    </Tooltip>
                  )}
                  {original?.effective_status === 'PENDING_REVIEW' && (
                    <Text fontSize={'14px'}>{getStatusMessage(original?.effective_status)}</Text>
                  )}
                </Flex>
              )}
            </Flex>
          );
        }
      },
      {
        Header: "Spend",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original?.insight?.spend ? `${currencySymbol}${Number(original.insight.spend).toFixed(2)}` : '-'}
            </Text>
          </Flex>
        )
      },
      {
        Header: "Cost per result",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original?.insight?.cpr ? `${currencySymbol}${Number(original.insight.cpr).toFixed(2)}` : '-'}
            </Text>
          </Flex>
        )
      },
      {
        Header: "Results",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const { insight, adsets = [] } = original;

          const eventNameMap = {
            destination_type: {
              ON_AD: "Meta Leads",
              App: "In-app subscribes",
            },
            optimization_goal: {
              APP_INSTALLS: "Mobile App Installation",
              LEAD_GENERATION: "Leads",
              THRUPLAY: "Thruplay",
              LINK_CLICKS: "Link Clicks",
              PAGE_LIKES: "Follows or Likes",
              QUALITY_CALL: "20-second Call",
              REACH: 'Reach',
              IMPRESSIONS: 'Reach'
            },
          };

          let derivedEventName = null;
          for (const adset of adsets) {
            if (eventNameMap.destination_type[adset.destination_type]) {
              derivedEventName = eventNameMap.destination_type[adset.destination_type];
              break;
            }
            if (eventNameMap.optimization_goal[adset.optimization_goal]) {
              derivedEventName = eventNameMap.optimization_goal[adset.optimization_goal];
              break;
            }
          }

          const originalEventName = insight?.eventName
            ? insight.eventName.charAt(0).toUpperCase() + insight.eventName.slice(1)
            : null;

          return (
            <Flex direction="column" mb={-4}>
              <Text fontSize="14px">
                {insight?.results ? insight.results : "-"}
              </Text>
              {(derivedEventName || originalEventName) && (
                <Text fontSize="10px" color="gray.500">
                  {derivedEventName || originalEventName}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        Header: "CPC",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.cpc ? `${currencySymbol}${Number(original.insight?.cpc).toFixed(2)}` : '-'}</Text>
          </Flex>
        )
      },
      {
        Header: "CTR",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex>
            <Text fontSize={'14px'}>
              {original.insight?.ctr ? `${Number(original.insight?.ctr).toFixed(2)}%` : '-'}
            </Text>
          </Flex>
        )
      },
      {
        Header: "CPM",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.cpm ? Number(original.insight?.cpm).toFixed(2) : '-'}</Text>
          </Flex>
        )
      },
      {
        Header: "Clicks",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.clicks ? original.insight?.clicks : '-'}</Text>
          </Flex>
        )
      },
      {
        Header: "Purchase Value",
        accessor: "",
        Filter: "",
        filter: "",
        width: 180,
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original.insight?.purchaseValue && original.insight.purchaseValue !== '-'
                ? `${currencySymbol}${parseFloat(original.insight.purchaseValue).toFixed(2)}`
                : original.insight?.purchaseValue || '-'}
            </Text>
          </Flex>
        )
      },
      {
        Header: "Roas",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          const roas = original.insight?.roas;
          const formattedROAS = (roas && !isNaN(parseFloat(roas)))
            ? parseFloat(roas).toFixed(2)
            : '-';
          return (
            <Flex>
              <Text fontSize={'14px'}>
                {formattedROAS}
              </Text>
            </Flex>
          );
        }
      },
      {
        Header: "hook Rate",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.hookRate ? `${Number(original.insight?.hookRate).toFixed(2)}%` : '-'}</Text>
          </Flex>
        )
      },
      {
        Header: "hold Rate",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.holdRate ? `${Number(original.insight?.holdRate).toFixed(2)}%` : '-'}</Text>
          </Flex>
        )
      },
      {
        Header: "Objective",
        accessor: "objective",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          let objective = '';
          if (original.objective === 'OUTCOME_SALES') objective = 'Sales';
          else if (original.objective === 'OUTCOME_LEADS') objective = 'Leads';
          else if (original.objective === 'OUTCOME_APP_PROMOTION') objective = 'App promotion';
          else if (original.objective === 'OUTCOME_AWARENESS') objective = 'Awareness';
          else if (original.objective === 'OUTCOME_ENGAGEMENT') objective = 'Engagement';
          else if (original.objective === 'OUTCOME_TRAFFIC') objective = 'Traffic';

          return <Text fontSize={'14px'}>{objective}</Text>;
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          let statusCopy = '';
          if (original.status === 'ARCHIVED') {
            statusCopy = 'PUBLISHED';
          } else {
            statusCopy = original.status;
          }
          return <Text fontSize={'14px'}>{statusCopy}</Text>;
        },
      },
      {
        Header: "Created Date",
        accessor: "createdDate",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Text fontSize={'14px'}>{moment(original.created_time).format("DD-MM-YYYY hh:mm A")}</Text>
        ),
      },
      {
        Header: "Updated Date",
        accessor: "updatedDate",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Text fontSize={'14px'}>{moment(original.updated_time).format("DD-MM-YYYY hh:mm A")}</Text>
        ),
      },
      {
        Header: "Actions",
        accessor: "",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'} alignItems={'center'}>
            <Tooltip label='Delete' fontSize='xs'>
              <Flex>
                <Icon
                  cursor={userInfo?.role === 'client' ? "pointer" : "not-allowed"}
                  color={userInfo?.role === 'client' ? "red.500" : "gray.300"}
                  as={MdDelete}
                  onClick={() => {
                    if (userInfo?.role === 'client') {
                      setSelectedCampaign(original);
                      setIsConfirmationModalOpen(true);
                    }
                  }}
                />
              </Flex>
            </Tooltip>
          </Flex>
        )
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCampaignId, adsAccountData, switchStates, userInfo?.role]
  );

  const confirmDelete = () => {
    if (selectedCampaign) {
      handleDeleteCampaign(selectedCampaign);
      setIsConfirmationModalOpen(false);
      setSelectedCampaign(null);
    }
  };

  // Updated to accept createdId and start polling
  const handleOnSuccess = ({ createdId }) => {
    setPendingCreatedId(createdId);
    setPendingTries(0);
    if (!isCampaignListUninitialized) {
      refetchCampaignsList(); // immediate pass
    }
  };

  const handleUpdateOnSuccess = () => {
    if (!isCampaignListUninitialized) {
      refetchCampaignsList(); // immediate pass
    }
  }

  return (
    <>
      <Flex mb={2}>
        <Button
          leftIcon={<AddIcon boxSize={3} />}
          colorScheme="brand"
          size="sm"
          onClick={() => setIsCreateFormVisible(true)}
        >
          Create
        </Button>
      </Flex>

      {isCreateFormVisible && (
        <CreateCampaignForm
          adsAccountData={adsAccountData}
          isOpen={isCreateFormVisible}
          onClose={() => setIsCreateFormVisible(false)}
          onSuccess={handleOnSuccess} // expects { createdId }
        />
      )}

      <DeleteConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this Campaign? This action can't be undone."
      />

      {/* DUPLICATE CAMPAIGN MODAL */}
      <Modal
        size={"xl"}
        isCentered={true}
        isOpen={isDuplicateModalOpen}
        scrollBehavior={"inside"}
        onClose={onDuplicateModalClose}
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={'md'}>Duplicate your campaign</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex gap={1} direction={'column'}>
              <Text fontSize={'14px'} fontWeight={'bold'}>Number of copies</Text>
              <NumberInput
                value={copyCount}
                onChange={(valueString, valueNumber) => setCopyCount(valueNumber)}
                min={1}
                size="sm"
                width={'530px'}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper border={'none'} />
                  <NumberDecrementStepper border={'none'} />
                </NumberInputStepper>
              </NumberInput>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button size={'sm'} mr={3} onClick={onDuplicateModalClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" isLoading={isCampaignDuplicating} size={'sm'} onClick={handleDuplicateCampaign}>
              Duplicate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* EDIT CAMPAIGN MODAL */}
      <Modal
        size={"2xl"}
        isCentered={true}
        isOpen={isOpen}
        scrollBehavior={"inside"}
        onClose={onClose}
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Campaign</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UpdateCampaignForm
              onClose={onClose}
              selectedCampaign={selectedCampaign}
              adsAccountData={adsAccountData}
              onSuccess={handleUpdateOnSuccess}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {campaignsListError || campaignsInsightsError ? (
        <Flex justifyContent={'center'} alignItems={'center'} height={'50vh'}>
          <Text color="red.500">
            {campaignsListError?.data?.message || campaignsInsightsError?.data?.message || "Sorry, something went wrong."}
          </Text>
        </Flex>
      ) : (isCampaignListLoading || isCampaignInsightsLoading || isLoadingCampaignsListWithInsights) ? (
        <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'50vh'}>
          <Spinner />
          <Text>Loading…</Text>
        </Flex>
      ) : (
        <AppTableWithPageMeta
          key={adsAccountData?.account_id || 'no-account'} 
          columns={columns}
          adsAccountId={adsAccountData?.account_id}
          datePreset={datePreset}
          currencyCode={currencyCode}
          data={campaignsListWithInsights || []}
          searchEnabled={!isSearchDisabled}
          initialState={{ pageIndex: currentPage }}
          pageMeta={campaignsList?.meta || {}}
          onPageChange={handlePageChange}
          onSearchInputChange={setSearchValue}
          searchTerm={searchValue}
          isFetching={isCampaignListFetcing || isCampaignInsightsListFetcing}
        />
      )}
    </>
  );
}

export default CampaignsTable;
